import React, { useEffect, useState, useRef } from "react";
import { Col, Container, FormControl, Row, Table } from "react-bootstrap";
import Sidebar from "../../commonAdmin/Sidebar";
import Header from "../../commonAdmin/Header";
import Footer from "../../commonAdmin/Footer";
import Loader from "../../../common/Loader";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import delivaApiService from "../../../../common/services/apiService";
import { ReactComponent as NoRecordFound } from "../../../../assets/images/NoRecordPlaceholder.svg";
import {
  getStatusBackgroundColor,
  getStatusClassName,
  showErrorToast,
  showSuccessToast,
} from "../../../../common/helpers/Utils";
import AddEmployee from "./AddEmployee";
import "./OrganizationDetails.scss";
import { Link } from "@material-ui/core";

const OrganizationDetails = (props) => {
  const { id: orgId } = useParams();
  const isMountedRef = useRef(true);

  // State management
  const [loading, setLoading] = useState(false);
  const [organizationDetails, setOrganizationDetails] = useState(null);
  const [employeeList, setEmployeeList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pageno, setPageno] = useState(1);
  const [pageArray, setPageArray] = useState([]);
  const [totalPage, setTotalPage] = useState(0);
  const [records, setRecords] = useState(10);
  const [inputRecords, setInputRecords] = useState(10);
  const [totalRecords, setTotalRecords] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [sortBy, setSortBy] = useState("userId");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [search, setSearch] = useState("");
  const history = useHistory();

  // Fetch organization details and employees
  useEffect(() => {
    const fetchData = async () => {
      if (orgId) {
        await getOrganizationDetails(orgId);
        await getEmployeeList(orgId);
      }
    };

    fetchData();

    return () => {
      isMountedRef.current = false;
    };
  }, [orgId]);

  useEffect(() => {
    const fetchEmployees = async () => {
      if (orgId) {
        await getEmployeeList(orgId);
      }
    };

    fetchEmployees();
  }, [sortBy, sortOrder, pageno, records]);

  // Get organization details
  const getOrganizationDetails = async (orgId) => {
    try {
      setLoading(true);
      const payload = {
        orgId: orgId,
      };
      const { data: response } = await delivaApiService(
        "getOrganizationDetails",
        payload,
      );

      if (isMountedRef.current && response?.status === 200) {
        const data = response?.resourceData?.resourceData;
        setOrganizationDetails({
          name: data?.name || "--",
          email: data?.email || "--",
          employeeCount: data?.totalEmployees || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching organization details:", error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Get employee list
  const getEmployeeList = async (orgId, searchValue = "") => {
    try {
      const payload = {
        orgId: orgId,
        sortBy: sortBy,
        sortOrder: sortOrder,
        page: pageno - 1,
        size: records,
        search: searchValue.trim(),
      };
      const { data: response } = await delivaApiService(
        "getAllOrganizationEmployee",
        payload,
      );

      if (isMountedRef.current && response?.status === 200) {
        setEmployeeList(response?.resourceData?.resourceData || []);
        const totalRecords = response?.resourceData?.totalRecords || 0;
        const totalPages = records ? Math.ceil(totalRecords / records) : 1;
        setTotalPage(totalPages);
        setPageArray(generatePageArray(totalPages, pageno));
        setTotalRecords(totalRecords);
      }
    } catch (error) {
      console.error("Error fetching employee list:", error);
    }
  };

  // Handle open modal
  const handleOpenModal = (employee = null) => {
    setSelectedEmployee(employee);
    setIsModalOpen(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
  };

  // Handle successful employee addition/update
  const handleEmployeeSuccess = async () => {
    await getEmployeeList(orgId);
    await getOrganizationDetails(orgId);
    handleCloseModal();
  };

  // Delete employee
  const handleDeleteEmployee = async (employeeId) => {
    try {
      setLoading(true);
      const payload = {
        orgEmployeeId: employeeId,
        orgId: orgId,
      };
      const { data: response } = await delivaApiService(
        "deleteOrganizationEmployee",
        payload,
      );

      if (isMountedRef.current) {
        if (response?.resourceData?.status === 200) {
          await getEmployeeList(orgId);
          await getOrganizationDetails(orgId);
          showSuccessToast(
            response?.resourceData?.responseMessage ||
              "Employee deleted successfully.",
          );
        } else {
          showErrorToast(
            response?.resourceData?.responseMessage ||
              "Failed to delete employee. Please try again.",
          );
        }
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Block/Unblock employee
  const handleBlockOrUnblockEmployee = async (employeeId, currentStatus) => {
    try {
      setLoading(true);
      const payload = {
        orgEmployeeId: employeeId,
        isBlocked: !currentStatus,
        orgId: orgId,
      };
      const { data: response } = await delivaApiService(
        "blockOrUnblockEmployee",
        payload,
      );

      if (isMountedRef.current) {
        if (response?.status === 200) {
          await getEmployeeList(orgId);
        }
      }
    } catch (error) {
      console.error("Error updating employee status:", error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSortChange = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
      console.log("sorting.....", sortOrder);
    } else {
      setSortBy(column);
      setSortOrder("ASC");
    }
  };

  // Prepare pagination array as per available records
  const generatePageArray = (totalPages, currentPage) => {
    let startPage, endPage;
    switch (true) {
      case totalPages <= 5:
        startPage = 1;
        endPage = totalPages;
        break;

      case currentPage <= 3:
        startPage = 1;
        endPage = 5;
        break;

      case currentPage + 1 >= totalPages:
        startPage = totalPages - 4;
        endPage = totalPages;
        break;

      default:
        startPage = currentPage - 2;
        endPage = currentPage + 2;
        break;
    }
    const pageArray = [];
    for (let i = startPage; i <= endPage; i++) {
      pageArray.push(i);
    }
    return pageArray;
  };

  // Manage switch pages through Pagination
  const onChangePage = (page) => {
    if (page >= 1 && page <= totalPage) {
      setPageno(page);
      localStorage.setItem("pageno", JSON.stringify(page));
    }
  };

  // Set No. of records per page
  const onChangeItemPerPage = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "" || /^[1-9][0-9]*$/.test(rawValue)) {
      setInputRecords(rawValue);
    }
  };

  const onEnterPerPage = (e) => {
    if (e.key === 'Enter') {
      const val = inputRecords;
      if (val && val > 0) {
        setRecords(parseInt(val));
        setPageno(1);
        localStorage.setItem("pageno", JSON.stringify(1));
      }
    }
  };

  const handleChangeInput = (e) => {
    let value = e.target.value;
    let trimmedValue = value.replace(/\s+/g, " ").trimStart();
    setSearch(trimmedValue);
    if (
      trimmedValue.trim() === "" &&
      trimmedValue === "" &&
      value?.[0] !== " "
    ) {
      getEmployeeList(orgId, trimmedValue);
    }
  };

  const handleEnter = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      getEmployeeList(orgId, search);
    }
  };

  const handleSearch = () => {
    if (search.trim() !== "") {
      getEmployeeList(orgId, search);
    }
  };

  // Capitalize first letter of a string
  const capitalizeFirst = (str) =>
    str ? str[0].toUpperCase() + str.slice(1).toLowerCase() : str;

  return (
    <div className="page__wrapper h-100">
      <Header {...props} />
      <div className="mainContainer CustomerTable">
        <Row noGutters className="h-100">
          <Sidebar {...props} />
          <Col md={9} lg={10} className="boxTyp3">
            <Container fluid>
              <div className="content-wrapper">
                {/* Organization Details Section */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <h3 className="font-weight-normal mb-0 line-ht fs-24 color-33 text-nowrap">
                    Organization Details
                  </h3>
                  <div className="mxw-90">
                    <FormControl
                      type="text"
                      placeholder="Search by Code/Name/Email/Contact"
                      className="searchTxt mb-3 mb-lg-0"
                      value={search}
                      onChange={(e) => handleChangeInput(e)}
                      onKeyDown={handleEnter}
                    />
                    <span
                      className="icon-search"
                      onClick={() => handleSearch()}
                    ></span>
                  </div>
                </div>
                <h5 className="font-weight-normal mb-3 fs18 color-grey">
                  <span
                    onClick={() => history.push(`/organizationList`)}
                    className="pointer"
                  >
                    Organization
                  </span>
                  <span className="icon-next mx-2 Fs12"></span>
                  {"Organization Details"}
                </h5>
                <section className="details-wrap mb-4">
                  <Row className="px-3 px-md-4">
                    <Col xs={12} md={6} lg={3} className="mb-4">
                      <h5 className="font-weight-normal color-grey fs18">
                        Organization Name
                      </h5>
                      <p className="fs18 font-weight-500 mb-0 gray-600 text-capitalize">
                        {organizationDetails?.name
                          ? organizationDetails?.name
                          : "--"}
                      </p>
                    </Col>
                    <Col xs={12} md={6} lg={3} className="mb-4">
                      <h5 className="font-weight-normal color-grey fs18">
                        Organization Email
                      </h5>
                      <p className="fs18 font-weight-500 mb-0 gray-600">
                        {organizationDetails?.email
                          ? organizationDetails?.email
                          : "--"}
                      </p>
                    </Col>
                    <Col xs={12} md={6} lg={3} className="mb-4">
                      <h5 className="font-weight-normal color-grey fs18">
                        Total Employee
                      </h5>
                      <p className="fs18 font-weight-500 mb-0 gray-600">
                        {organizationDetails?.employeeCount
                          ? organizationDetails?.employeeCount
                          : "0"}
                      </p>
                    </Col>
                  </Row>
                </section>

                <div className="page-header d-flex mb-3">
                  <div className="page-search d-flex justify-content-between  gap-3 w-100">
                    <div className="d-flex align-item-center">
                      <h3 className="font-weight-normal my-2 mb-0 line-ht fs-24 color-33 text-nowrap">
                        List of employee{" "}
                        <span className="color-grey">
                          ({totalRecords || 0})
                        </span>
                      </h3>
                    </div>
                    <button
                      className="button rounded-pill btnPrimary text-nowrap px-4 py-2"
                      onClick={handleOpenModal}
                    >
                      <span className="fs20">+</span> Add Employee
                    </button>
                  </div>
                </div>

                {employeeList?.length > 0 ? (
                  <>
                    <Table responsive>
                      <thead>
                        <tr>
                          <th></th>
                          <th>
                            Partner code{" "}
                            <Link
                              className="icon-up-and-down color-light-grey"
                              onClick={() => handleSortChange("userId")}
                            />
                          </th>
                          <th>
                            Name{" "}
                            <Link
                              className="icon-up-and-down color-light-grey"
                              onClick={() => handleSortChange("name")}
                            />
                          </th>
                          <th>Contact & Email</th>
                          <th>Address</th>
                          <th>User Status</th>
                          <th>Kyc Status</th>
                          <th>Banking Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody className="ttl-order-table">
                        {employeeList.map((employee, index) => (
                          <tr key={"employee" + index}>
                            <td style={{ maxWidth: "25px" }}></td>
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span>
                                  {employee?.userId
                                    ? `#${employee?.userId}`
                                    : "--"}
                                </span>
                                {employee?.admin && (
                                  <span
                                    style={{
                                      backgroundColor: "#FEC92D",
                                      color: "#085133",
                                      padding: "2px 8px",
                                      borderTopLeftRadius: "25px 12px",
                                      borderBottomLeftRadius: "25px 12px",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    ADMIN
                                  </span>
                                )}
                              </div>
                            </td>

                            <td>{employee?.name ? employee?.name : "--"}</td>
                            <td>
                              {employee?.contactAndEmail
                                ? employee?.contactAndEmail
                                : "--"}
                            </td>
                            <td>
                              {employee?.address ? employee?.address : "--"}
                            </td>
                            <td
                              className={getStatusClassName(
                                capitalizeFirst(employee?.userStatus),
                              )}
                            >
                              <div className="flex-active">
                                {employee?.userStatus && (
                                  <span
                                    className="ab-dot"
                                    style={getStatusBackgroundColor(
                                      capitalizeFirst(employee?.userStatus),
                                    )}
                                  ></span>
                                )}

                                {employee?.userStatus
                                  ? capitalizeFirst(employee?.userStatus)
                                  : "--"}
                              </div>
                            </td>
                            <td
                              className={getStatusClassName(
                                employee?.kycStatus,
                              )}
                            >
                              <div className="flex-active">
                                {employee?.kycStatus && (
                                  <span
                                    className="ab-dot"
                                    style={getStatusBackgroundColor(
                                      employee?.kycStatus,
                                    )}
                                  ></span>
                                )}

                                {employee?.kycStatus
                                  ? employee?.kycStatus
                                  : "--"}
                              </div>
                            </td>
                            <td
                              className={getStatusClassName(
                                employee?.bankingStatus,
                              )}
                            >
                              <div className="flex-active">
                                {employee?.bankingStatus && (
                                  <span
                                    className="ab-dot"
                                    style={getStatusBackgroundColor(
                                      employee?.bankingStatus,
                                    )}
                                  ></span>
                                )}

                                {employee?.bankingStatus
                                  ? employee?.bankingStatus
                                  : "--"}
                              </div>
                            </td>
                            {/* <td>
                              {employee?.lastLogin
                                ? moment(employee?.lastLogin).format(
                                    "DD MMM YYYY | h:mm A",
                                  )
                                : "--"}
                            </td> */}
                            <td>
                              {!employee?.userId ? (
                                <a
                                  className="pointer"
                                  onClick={() =>
                                    handleDeleteEmployee(employee.orgEmployeeId)
                                  }
                                >
                                  <span title="Delete" className="text-nowrap">
                                    <i className="icon-bin"></i>
                                    {/* &nbsp; Delete */}
                                  </span>
                                </a>
                              ) : employee.blocked ? (
                                <a
                                  className="pointer"
                                  onClick={() =>
                                    handleBlockOrUnblockEmployee(
                                      employee.orgEmployeeId,
                                      true,
                                    )
                                  }
                                >
                                  <span title="Block" className="text-nowrap">
                                    <i className="icon-password"></i>
                                    {/* &nbsp; Block */}
                                  </span>
                                </a>
                              ) : (
                                <a
                                  className="pointer"
                                  onClick={() =>
                                    handleBlockOrUnblockEmployee(
                                      employee.orgEmployeeId,
                                      false,
                                    )
                                  }
                                >
                                  <span title="Unblock" className="text-nowrap">
                                    <i className="icon-unblockicon"></i>
                                    {/* &nbsp; Unblock */}
                                  </span>
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {employeeList?.length > 0 && (
                      <nav
                        aria-label="Page navigation "
                        className="position-relative"
                      >
                        <ul className="pagination pg-blue deliva-pagination justify-content-end">
                          <li className="page-item">
                            <button
                              className="page-link rotate-180 control-btn"
                              aria-label="Previous"
                              onClick={() => onChangePage(pageno - 1)}
                              disabled={pageno === 1}
                              // disabled={!ref?.active}
                            >
                              <span className="icon-next"></span>
                              <span className="sr-only">Previous</span>
                            </button>
                          </li>

                          {pageArray?.map((page, ind) => (
                            <li key={ind} className="page-item">
                              <a
                                className={`page-link ${
                                  pageno === page ? "active" : ""
                                }`}
                                onClick={() => onChangePage(page)}
                              >
                                {page}
                              </a>
                            </li>
                          ))}

                          <li className="page-item">
                            <button
                              className="page-link control-btn"
                              aria-label="Next"
                              onClick={() => onChangePage(pageno + 1)}
                              disabled={pageno === totalPage}
                            >
                              <span className="icon-next"></span>
                              <span className="sr-only">Next</span>
                            </button>
                          </li>

                          <li className="page-value">
                            Enter Per Page{" "}
                            <input
                              type="text"
                              placeholder="10"
                              name="records"
                              value={inputRecords}
                              onChange={onChangeItemPerPage}
                              onKeyDown={onEnterPerPage}
                            />
                          </li>
                        </ul>
                      </nav>
                    )}
                  </>
                ) : (
                  <div className="d-lg-flex-column justify-content-center pt-4 align-items-center h-100 w-100 text-center">
                    <NoRecordFound />
                  </div>
                )}
              </div>
            </Container>

            {/* Add/Edit Employee Modal */}
            {isModalOpen && (
              <AddEmployee
                orgId={orgId}
                employee={selectedEmployee}
                onSuccess={handleEmployeeSuccess}
                onClose={handleCloseModal}
                onHide={handleCloseModal}
              />
            )}

            <Footer {...props} />
          </Col>
        </Row>
      </div>
      <Loader showLoader={loading} />
    </div>
  );
};

export default OrganizationDetails;
